import { z } from "zod";
import { ACTION_CATEGORY_LIST } from "./action-categories";
import { idSchema } from "./ids";
import { iconDataUrlSchema, parseSiteAddress, URL_LIMIT } from "./site-preview";

/**
 * Teammates have no table yet. Grants store these keys, checked against this one list, which the
 * sidebar also reads. When a teammates table exists the key becomes a foreign key.
 */
export const TEAMMATES = [
  { key: "ada", name: "Ada" },
  { key: "iris", name: "Iris" },
  { key: "rowan", name: "Rowan" },
] as const;
export const teammateKeySchema = z.enum(["ada", "iris", "rowan"]);
export type TeammateKey = z.infer<typeof teammateKeySchema>;

export const decisionSchema = z.enum(["always", "ask_first", "never"]);
export type Decision = z.infer<typeof decisionSchema>;

export const actionCategorySchema = z.enum(ACTION_CATEGORY_LIST);
export type ActionCategory = z.infer<typeof actionCategorySchema>;
export const CATEGORY_ORDER = actionCategorySchema.options;

/** One vocabulary for every app, and later for website logins (which add Legal). */
export const ACTION_CATEGORIES: Record<
  ActionCategory,
  { label: string; description: string; defaultDecision: Decision }
> = {
  look: {
    label: "Look and download",
    description: "Read records, reports, mail and files.",
    defaultDecision: "always",
  },
  change: {
    label: "Fill in or change",
    description: "Create or edit records, drafts, labels and events.",
    defaultDecision: "ask_first",
  },
  send: {
    label: "Send messages",
    description: "Send, reply, forward or answer invitations.",
    defaultDecision: "ask_first",
  },
  money: {
    label: "Money",
    description: "Payments, deposits, transfers and charges.",
    defaultDecision: "never",
  },
  settings: {
    label: "Settings and sharing",
    description: "Account settings, rules, forwarding and permissions.",
    defaultDecision: "never",
  },
  delete: {
    label: "Delete",
    description: "Delete, trash or change many records at once.",
    defaultDecision: "never",
  },
  others: {
    label: "Other people's data",
    description: "Reach another person's mailbox, calendar or files.",
    defaultDecision: "never",
  },
};

export const connectionScopeSchema = z.enum(["workspace", "personal"]);
export type ConnectionScope = z.infer<typeof connectionScopeSchema>;

export const connectionStatusSchema = z.enum([
  "connecting",
  "connected",
  "needs_renewal",
  "paused",
  "revoked",
  "disconnected",
]);
export type ConnectionStatus = z.infer<typeof connectionStatusSchema>;

/** States an account can't be used in until someone signs in again: signed out, revoked, or mid sign-in. */
const RENEWAL_STATUSES: ReadonlySet<ConnectionStatus> = new Set([
  "needs_renewal",
  "revoked",
  "connecting",
]);

/** Whether an account in this state needs signing in again before anything can use it. */
export const needsRenewal = (status: ConnectionStatus): boolean => RENEWAL_STATUSES.has(status);

/** An account's rule for a kind of action with Look only applied: Never to everything but looking. */
export const ruleWithLookOnly = (
  lookOnly: boolean,
  category: ActionCategory,
  decision: Decision,
): Decision => (lookOnly && category !== "look" ? "never" : decision);

const revisionSchema = z.number().int().nonnegative();
const isoDate = z.string().min(1);

export const connectionAppSchema = z.object({
  slug: z.string().min(1).max(100),
  name: z.string().min(1),
  logo: z.string().nullable(),
});
export type ConnectionApp = z.infer<typeof connectionAppSchema>;

export const connectionCatalogSchema = z.object({
  /** False when the API has no Composio key; the page says connections aren't set up. */
  configured: z.boolean(),
  processingRegion: z.literal("us"),
  /** True when the workspace's region differs from where Composio processes data. */
  disclosure: z.boolean(),
  canCreateWorkspace: z.boolean(),
  apps: z.array(connectionAppSchema),
  /** False when the API has no key to encrypt passwords with; website logins can't be saved. */
  websiteLogins: z.boolean(),
  /** True for owners and admins, who may opt in to reading members' personal connections. */
  canViewMembersPersonal: z.boolean().default(false),
});
export type ConnectionCatalog = z.infer<typeof connectionCatalogSchema>;

/**
 * What an app can do before anyone signs in: how many of its current actions fall in each
 * category, every category listed in order. A count of 0 means the app has nothing there, so the
 * add dialog leaves the category out rather than promising a decision about nothing.
 */
export const appCategoriesSchema = z.object({
  app: z.string().min(1).max(100),
  categories: z.array(
    z.object({ key: actionCategorySchema, actions: z.number().int().nonnegative() }),
  ),
});
export type AppCategories = z.infer<typeof appCategoriesSchema>;

export const connectionSummarySchema = z.object({
  id: idSchema("connection"),
  app: connectionAppSchema,
  scope: connectionScopeSchema,
  ownerUserId: z.string().nullable(),
  mine: z.boolean(),
  label: z.string(),
  providerIdentity: z.string().nullable(),
  status: connectionStatusSchema,
  statusReason: z.string().nullable(),
  lookOnly: z.boolean(),
  /**
   * Whether people's chats may use this workspace connection; always false for a personal one,
   * which its owner's chats use anyway. Defaults for an API that predates it.
   */
  availableInChat: z.boolean().default(false),
  teammates: z.array(teammateKeySchema),
  revision: revisionSchema,
  checkedAt: isoDate.nullable(),
  createdAt: isoDate,
  can: z.object({ manage: z.boolean(), pause: z.boolean(), grant: z.boolean() }),
  /**
   * Who looks after it: renews it, resumes it, changes what it allows. Null from an API that
   * predates it.
   */
  custodianUserId: z.string().nullable().default(null),
  /**
   * Its rule for each kind of action, from our own policy rows with Look only applied, so a
   * playbook's step can say what it may do there without asking any app. A tool's own exception is
   * not read: a step names a kind of action, not a tool. Empty from an API that predates it.
   */
  rules: z.partialRecord(actionCategorySchema, decisionSchema).default({}),
});
export type ConnectionSummary = z.infer<typeof connectionSummarySchema>;
export const connectionListSchema = z.object({
  connections: z.array(connectionSummarySchema),
  /**
   * How many other members' personal connections exist, counted whether or not the list includes
   * them, so the page can offer the opt-in. Always 0 for a member, who is never told.
   */
  othersPersonalCount: z.number().int().nonnegative().default(0),
});

/**
 * Owners and admins may ask to read other members' personal connections, read-only. Sent as
 * `?membersPersonal=true`; a query string carries text, so `stringbool` reads "true" and "false"
 * and refuses anything else. A member who sends it is refused FORBIDDEN, not ignored.
 */
export const membersPersonalQuerySchema = z.object({
  membersPersonal: z.stringbool({ truthy: ["true"], falsy: ["false"] }).optional(),
});

export const credentialSchema = z.object({
  id: idSchema("credential"),
  kind: z.enum(["composio", "website_login"]),
  status: connectionStatusSchema,
  statusReason: z.string().nullable(),
  toolkitVersion: z.string().nullable(),
  checkedAt: isoDate.nullable(),
  createdAt: isoDate,
});
export const connectionDetailSchema = connectionSummarySchema.extend({
  credentials: z.array(credentialSchema),
});
export type ConnectionDetail = z.infer<typeof connectionDetailSchema>;

export const actionSchema = z.object({
  tool: z.string().min(1),
  name: z.string(),
  category: actionCategorySchema,
  /** The effective decision after override, category and Look only. */
  decision: decisionSchema,
  override: decisionSchema.nullable(),
  /** Which rule or exception placed the tool, e.g. "rule: send". */
  reason: z.string(),
});
export const permissionsSchema = z.object({
  revision: revisionSchema,
  lookOnly: z.boolean(),
  categories: z.array(
    z.object({
      key: actionCategorySchema,
      decision: decisionSchema,
      actions: z.array(actionSchema),
    }),
  ),
});
export type Permissions = z.infer<typeof permissionsSchema>;

const MAX_OVERRIDES = 500;
export const updatePermissionsInputSchema = z.object({
  revision: revisionSchema,
  lookOnly: z.boolean(),
  /** Exhaustive: zod 4 requires every enum key. */
  categories: z.record(actionCategorySchema, decisionSchema),
  /** A null value clears that action's override. */
  overrides: z
    .record(z.string().min(1).max(200), decisionSchema.nullable())
    .refine((overrides) => Object.keys(overrides).length <= MAX_OVERRIDES, {
      message: `At most ${MAX_OVERRIDES} overrides per update.`,
    }),
});
export type UpdatePermissionsInput = z.infer<typeof updatePermissionsInputSchema>;

export const startAttemptInputSchema = z.object({
  app: z.string().min(1).max(100),
  scope: connectionScopeSchema,
  /** Set when renewing access on an existing connection. */
  connectionId: idSchema("connection").optional(),
  /**
   * "desktop": the sign-in opens in the system browser, outside the app, so the provider returns
   * to a page that needs no session and only says to go back. The app's own polling finishes it.
   */
  returnTo: z.enum(["desktop"]).optional(),
});
/** Why an attempt failed, when the person can act on it. */
export const attemptFailureReasonSchema = z.enum(["different_account"]);
export type AttemptFailureReason = z.infer<typeof attemptFailureReasonSchema>;

export const attemptSchema = z.object({
  id: idSchema("attempt"),
  status: z.enum(["waiting", "connected", "failed", "expired"]),
  /** Opened in the browser, so only http(s): a `javascript:` URL must never parse. */
  redirectUrl: z.url({ protocol: /^https?$/ }),
  expiresAt: isoDate,
  connectionId: idSchema("connection").nullable(),
  /**
   * True when the attempt renews an existing connection, so finishing it has nothing left to set
   * up. Defaults for an API that predates it.
   */
  renewal: z.boolean().default(false),
  /** "different_account": a renewal signed in as another account than the connection's. */
  reason: attemptFailureReasonSchema.nullable().optional(),
});
export type Attempt = z.infer<typeof attemptSchema>;

export const labelInputSchema = z.object({
  revision: revisionSchema,
  label: z.string().trim().min(1).max(80),
});
export const teammatesInputSchema = z.object({
  revision: revisionSchema,
  teammates: z.array(teammateKeySchema).max(TEAMMATES.length),
});
/**
 * The "Available in chat" switch on a workspace connection; managers only. `revision` is the
 * revision the switch was read at: a switch sent from a stale read is a conflict.
 */
export const availableInChatInputSchema = z.object({
  revision: revisionSchema,
  availableInChat: z.boolean(),
});

export const connectionEventKindSchema = z.enum([
  "connected",
  "renewed",
  "paused",
  "resumed",
  "disconnected",
  "relabeled",
  "access_changed",
  "permissions_changed",
  "tool_call",
  "tool_denied",
  "approval_requested",
  "approval_decided",
  "standing_approval_created",
  "standing_approval_used",
  "standing_approval_revoked",
  "availability_changed",
]);
export type ConnectionEventKind = z.infer<typeof connectionEventKindSchema>;

/**
 * What an event recorded beyond its columns, flat so it stays queryable. The keys each kind
 * writes (older rows may have none, or older keys):
 * - connected, renewed: `identity` (the account it signed in as, or null), `identityFound`;
 *   renewed also `identityChanged`
 * - relabeled: `from`, `to` (the names)
 * - permissions_changed, one event per thing changed: `target` ("category" | "lookOnly" |
 *   "override"), `key` (the category or tool), `from`, `to` (decisions, booleans for Look only,
 *   null for "no exception")
 * - approval_decided: `teammate`, `remember`, `requestedAt`; a chat's also `standing` and
 *   `threadId`
 * - standing_approval_created: `standingApprovalId`, `threadId`, `approvalId` (the one answered)
 * - standing_approval_revoked: `standingApprovalId`, `threadId`; `reason` "account_changed" when
 *   a new choice replaced one left on an account the connection no longer holds, and
 *   "chat_switched_off" when switching the connection out of chat revoked it
 * - standing_approval_used: `standingApprovalId`, `via` ("chat"), `threadId`
 * - availability_changed: `availableInChat` ("true" | "false", the new value),
 *   `expiredApprovals` and `revokedStanding` (counts, as strings, of the chats' waiting approvals
 *   expired and choices revoked by switching it off; "0" when switching it on)
 * Never a tool's arguments or result: payloads are not kept in the audit trail.
 */
export const eventDetailSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);
export type EventDetail = z.infer<typeof eventDetailSchema>;

export const eventSchema = z.object({
  id: idSchema("event"),
  at: isoDate,
  actorKind: z.enum(["person", "teammate", "system"]),
  actor: z.string().nullable(),
  kind: connectionEventKindSchema,
  tool: z.string().nullable(),
  category: actionCategorySchema.nullable(),
  decision: decisionSchema.nullable(),
  outcome: z.string().nullable(),
  detail: eventDetailSchema.nullable().default(null),
  /** Composio's log reference for a tool call, for support. */
  logId: z.string().nullable().default(null),
});
export type ConnectionEvent = z.infer<typeof eventSchema>;
export const eventListSchema = z.object({ events: z.array(eventSchema) });

/**
 * Who asked: a teammate at work, a person's chat, or a step of a case (decision
 * `2026-11-02-a-case-runs-apart-from-the-chat-that-starts-it`).
 */
export const approvalContextKindSchema = z.enum(["teammate", "chat", "case"]);
export type ApprovalContextKind = z.infer<typeof approvalContextKindSchema>;
/**
 * The contexts whose approval belongs to one thread and one person who answers it: a chat's, and a
 * case step's. Only a chat's may be remembered "for this chat".
 */
export const THREAD_CONTEXT_KINDS = [
  "chat",
  "case",
] as const satisfies readonly ApprovalContextKind[];
export const isThreadContext = (kind: ApprovalContextKind): kind is "chat" | "case" =>
  kind === "chat" || kind === "case";

export const approvalStatusSchema = z.enum([
  "pending",
  "approved",
  "denied",
  "expired",
  "executing",
  "executed",
  "failed",
]);
export type ApprovalStatus = z.infer<typeof approvalStatusSchema>;

export const approvalSchema = z.object({
  id: idSchema("approval"),
  connectionId: idSchema("connection"),
  /** Defaults for an API that predates chat approvals. */
  contextKind: approvalContextKindSchema.default("teammate"),
  /** The chat that asked; null for a teammate's approval. */
  threadId: idSchema("thread").nullable().default(null),
  /** The one person who may answer a chat's approval; null for a teammate's approval. */
  approverUserId: z.string().nullable().default(null),
  /** Null when no teammate asked: a chat's approval. */
  teammate: teammateKeySchema.nullable(),
  tool: z.string().min(1),
  category: actionCategorySchema,
  arguments: z.record(z.string(), z.unknown()),
  requestedAt: isoDate,
  status: approvalStatusSchema,
});
export const approvalListSchema = z.object({ approvals: z.array(approvalSchema) });

/** How the card draws a field. The server decides this, never the browser. */
export const FIELD_KINDS = [
  "text",
  "longtext",
  "number",
  "boolean",
  "select",
  "datetime",
  "email",
  "uri",
  "chips",
  "readonly",
  "masked",
  "json",
] as const;
export const fieldKindSchema = z.enum(FIELD_KINDS);
export type FieldKind = z.infer<typeof fieldKindSchema>;

/** One argument of a proposed action, as the card shows it. */
export const fieldViewSchema = z.strictObject({
  key: z.string(),
  label: z.string(),
  help: z.string().optional(),
  kind: fieldKindSchema,
  required: z.boolean(),
  options: z.array(z.string()).optional(),
  /** Absent when `kind` is `masked`, and when the argument was not sent. */
  value: z.unknown().optional(),
  /** Revision 1's value, when this field was edited. Always absent until approvals can be edited. */
  was: z.unknown().optional(),
  readOnly: z.boolean(),
  /** A hint says changing this one deserves a second look. */
  material: z.boolean(),
});
export type FieldView = z.infer<typeof fieldViewSchema>;

/**
 * What a chat's approval will do, for the one person who may decide it. The chat module never holds
 * arguments (decision 66); this is the only place they leave Connections, and only to the approver.
 */
export const approvalCardSchema = z.strictObject({
  id: idSchema("approval"),
  status: approvalStatusSchema,
  revision: z.int().min(1),
  app: connectionAppSchema,
  /** The connection's label, which a person reviews the call against; null when it has none. */
  account: z.string().nullable(),
  tool: z.strictObject({ slug: z.string(), label: z.string() }),
  category: actionCategorySchema,
  /** Abacor's own line about the call, display-safe; the page marks it as Abacor's. */
  sentence: z.string(),
  /** Empty when `opaque`. */
  fields: z.array(fieldViewSchema),
  /** The tool describes no fields: the page shows `arguments`, read-only. */
  opaque: z.boolean(),
  /** What will be sent, without any masked value. */
  arguments: z.record(z.string(), z.unknown()),
  edited: z.strictObject({ by: z.string(), at: isoDate }).nullable(),
});
export type ApprovalCard = z.infer<typeof approvalCardSchema>;
export const approvalDecisionInputSchema = z.object({
  decision: z.enum(["approve", "deny"]),
  /**
   * On the connection for everyone: approve and also set this action's override to "always".
   * Only for someone who manages the connection.
   */
  remember: z.boolean().default(false),
  /**
   * For this chat: approve and stop asking for this action, in this category, in the chat that
   * asked, on the account the connection holds now. A chat's approval only, never with `remember`
   * or a denial, and only for Fill in or change and Send messages.
   */
  standing: z.boolean().default(false),
});

/** A "For this chat" choice still in force, for the person who chose it and the connection's managers. */
export const standingApprovalSchema = z.object({
  id: idSchema("standingApproval"),
  connectionId: idSchema("connection"),
  tool: z.string().min(1),
  category: actionCategorySchema,
  /** The chat it belongs to. */
  threadId: idSchema("thread"),
  chosenBy: z.string(),
  createdAt: isoDate,
  lastUsedAt: isoDate.nullable(),
  useCount: z.number().int().nonnegative(),
});
export type StandingApproval = z.infer<typeof standingApprovalSchema>;
export const standingApprovalListSchema = z.object({
  standingApprovals: z.array(standingApprovalSchema),
});
export const standingApprovalQuerySchema = z.object({
  connectionId: idSchema("connection").optional(),
  threadId: idSchema("thread").optional(),
});

/**
 * Website logins (spec §10, decisions 29 and 31): a connection of its own, a site, a username and
 * a password saved encrypted. The password goes in once and never comes back out: no answer
 * carries it, and replacing it is the only way to change it.
 */
export const websiteLoginStatusSchema = z.enum(["not_tried", "connected", "password_rejected"]);
export type WebsiteLoginStatus = z.infer<typeof websiteLoginStatusSchema>;

/**
 * A website login's state as an account's: a password the site rejected needs signing in again,
 * and one not yet tried reads as connected, since only a try can tell.
 */
export const websiteLoginAccountStatus = (status: WebsiteLoginStatus): ConnectionStatus =>
  status === "password_rejected" ? "needs_renewal" : "connected";

export const USERNAME_LIMIT = 320;
export const PASSWORD_LIMIT = 1024;
export const LABEL_LIMIT = 200;

const siteUrl = z
  .string()
  .max(URL_LIMIT)
  .refine((value) => parseSiteAddress(value).ok, { error: "Enter an https address." });
const password = z.string().min(1).max(PASSWORD_LIMIT);

export const websiteLoginSchema = z.object({
  id: idSchema("connection"),
  /** The page a teammate signs in on, as `https://host/path`. */
  url: z.string(),
  /** The host people recognise, without `www.`. */
  host: z.string(),
  label: z.string(),
  username: z.string(),
  scope: connectionScopeSchema,
  /** Whose personal login this is, as a connection's `ownerUserId`; null for a workspace login. */
  ownerUserId: z.string().nullable().default(null),
  /** Who looks after it, as a connection's `custodianUserId`. Null from an API that predates it. */
  custodianUserId: z.string().nullable().default(null),
  status: websiteLoginStatusSchema,
  /** The site's own icon, found by the server when the login was saved. */
  iconUrl: iconDataUrlSchema.nullable(),
  createdAt: isoDate,
  passwordSavedAt: isoDate,
  /** The auth service's ID for whoever saved the password; `passwordSavedByMe` says if it's you. */
  passwordSavedBy: z.string(),
  passwordSavedByMe: z.boolean(),
  /** Manage: replace the password. Remove: whoever manages it. */
  can: z.object({ manage: z.boolean(), remove: z.boolean() }),
  /** Sent back with a replacement, which is refused with CONFLICT if the login moved on. */
  revision: revisionSchema,
});
export type WebsiteLogin = z.infer<typeof websiteLoginSchema>;
export const websiteLoginListSchema = z.object({
  websiteLogins: z.array(websiteLoginSchema),
  /** As on the connection list: other members' personal logins, counted but not shown unless asked. */
  othersPersonalCount: z.number().int().nonnegative().default(0),
});

export const newWebsiteLoginInputSchema = z.object({
  url: siteUrl,
  username: z.string().trim().min(1).max(USERNAME_LIMIT),
  password,
  /** Empty to use the site's own name, or its host when it has none. */
  label: z.string().trim().max(LABEL_LIMIT),
  scope: connectionScopeSchema,
});
export type NewWebsiteLoginInput = z.infer<typeof newWebsiteLoginInputSchema>;

export const replacePasswordInputSchema = z.object({ password, revision: revisionSchema });
